import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AccountingApi } from '_api';
import { handleError, handleResponse } from '_helpers/api';
import { sleep } from '_helpers/common';
import { selectLoggedIn } from '_store/user/selector';

export const getChannel = createAsyncThunk(
  'chat/getChannel',
  async function ({ customerId, productId }, { rejectWithValue }) {
    try {
      const response = await AccountingApi.getChannel({ customerId, productId });
      const data = handleResponse(response);
      return { userId: data.userid, accessToken: data.accessToken, channelUrl: data.channelUrl };
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const getCountOfUnread = createAsyncThunk(
  'chat/getCountOfUnread',
  async function ({ customerId }, { rejectWithValue }) {
    try {
      const response = await AccountingApi.getCountOfUnread({ customerId });
      const data = handleResponse(response);
      return data?.count;
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const pollCountOfUnread = createAsyncThunk(
  'chat/pollCountOfUnread',
  async function ({ customerId }, { dispatch, getState }) {
    const loggedIn = selectLoggedIn(getState());

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (loggedIn) {
        dispatch(getCountOfUnread({ customerId }));
      }

      await sleep(5 * 60 * 1000);
    }
  }
);

const initialData = {
  userId: null,
  accessToken: null,
  channelUrl: null,
};

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    countOfUnread: 0,
    searchVisible: false,
    currentMessage: null,
    data: { ...initialData },
    error: null,
    loading: false,
  },
  reducers: {
    showSearch(state) {
      state.searchVisible = true;
    },
    hideSearch(state) {
      state.searchVisible = false;
    },
    setCurrentMessage(state, action) {
      if (action.payload) {
        state.currentMessage = action.payload;
      }
    },
    resetCurrentMessage(state) {
      state.currentMessage = null;
    },
    refreshData(state, action) {
      state.data = {
        ...state.data,
        ...action.payload,
      };
    },
    resetData(state) {
      state.data = { ...initialData };
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChannel.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getChannel.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(getChannel.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    builder.addCase(getCountOfUnread.fulfilled, (state, action) => {
      state.countOfUnread = action.payload;
    });
  },
});

export const { showSearch, hideSearch, setCurrentMessage, resetCurrentMessage, refreshData, resetData, resetError } =
  chatSlice.actions;

export default chatSlice.reducer;
